import React from 'react';

import { Col, FavoriteButton, Image, Link, Row, Text } from '@opendoor/bricks-next';

import { formatCurrency } from '../../helpers/formatters';

export type PropertyCardProps = {
  beds?: number | null;
  baths?: number | null;
  city?: string | null;
  dwellingType?: string | null;
  estimatedMonthlyPayment?: number | null;
  href?: string | null;
  isFavorite?: boolean | null;
  neighborhood?: string | null;
  onFavoritePress?: () => void;
  photoUrl?: string | null;
  price?: number | null;
  sqft?: number | null;
};

export const PropertyCard: React.FC<PropertyCardProps> = ({
  beds,
  baths,
  city,
  dwellingType,
  estimatedMonthlyPayment,
  href,
  isFavorite,
  neighborhood,
  onFavoritePress,
  photoUrl,
  price,
  sqft,
}) => {
  return (
    <Col width="100%" gap="$5">
      <Link href={href ?? undefined} analyticsName="marketplace-property-card-image-link">
        <Col width="100%" borderRadius="$8x" overflow="hidden">
          <Image
            alt="Main view of property"
            borderRadius="$8x"
            source={{ uri: photoUrl ?? undefined }}
            resizeMode="cover"
            aspectRatio={16 / 9}
          />
        </Col>
      </Link>
      <Col gap="$3">
        <Row alignItems="center" justifyContent="space-between">
          <Row alignItems="baseline" gap="$1">
            {price != null && (
              <Text tag="span" color="$contentPrimary" typography="$labelXlarge">
                {formatCurrency(price, { inDollars: true })}
              </Text>
            )}
            {estimatedMonthlyPayment != null && (
              <Text tag="span" color="$contentTertiary" typography="$bodySmall">
                {formatCurrency(estimatedMonthlyPayment, { inDollars: true })}/month est.
              </Text>
            )}
          </Row>
          {onFavoritePress && (
            <Row alignSelf="center" justifyContent="flex-end">
              <FavoriteButton
                isFavorited={isFavorite ?? false}
                analyticsName="marketplace-property-card-favorite-button"
                onPress={onFavoritePress}
                size="small"
                variant="default"
                alignSelf="center"
              />
            </Row>
          )}
        </Row>
        <Link href={href ?? undefined} analyticsName="marketplace-property-card-info-link">
          <Row alignItems="baseline" width="100%">
            <Text tag="span" typography="$bodySmall" color="$contentTertiary">
              {beds != null && `${beds} bds`}
            </Text>
            {baths != null && (
              <>
                <Text tag="span" typography="$bodySmall" color="$contentStateInactiveSecondary">
                  {' • '}
                </Text>
                <Text tag="span" typography="$bodySmall" color="$contentTertiary">
                  {baths} ba
                </Text>
              </>
            )}
            {sqft != null && (
              <>
                <Text tag="span" typography="$bodySmall" color="$contentStateInactiveSecondary">
                  {' • '}
                </Text>
                <Text tag="span" typography="$bodySmall" color="$contentTertiary">
                  {sqft.toLocaleString()} sqft
                </Text>
              </>
            )}
            {dwellingType && (
              <>
                <Text tag="span" typography="$bodySmall" color="$contentStateInactiveSecondary">
                  {' • '}
                </Text>
                <Text tag="span" typography="$bodySmall" color="$contentTertiary">
                  {dwellingType}
                </Text>
              </>
            )}
          </Row>
          <Row alignItems="baseline" gap="$half">
            {neighborhood && (
              <Text tag="span" typography="$bodySmall" color="$contentTertiary">
                {city ? `${neighborhood}, ` : neighborhood}
              </Text>
            )}
            {city && (
              <Text tag="span" typography="$bodySmall" color="$contentTertiary">
                {city}
              </Text>
            )}
          </Row>
        </Link>
      </Col>
    </Col>
  );
};
